import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: React.ReactNode;
    children: React.ReactNode;
    footer?: React.ReactNode;
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
    className?: string;
}

export const Modal: React.FC<ModalProps & { variant?: 'dark' | 'light' }> = ({
    isOpen,
    onClose,
    title,
    children,
    footer,
    size = 'md',
    className = '',
    variant = 'dark'
}) => {
    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };

        if (isOpen) {
            document.body.style.overflow = 'hidden';
            window.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const widthClasses = {
        sm: 'max-w-md',
        md: 'max-w-xl',
        lg: 'max-w-3xl',
        xl: 'max-w-5xl',
        full: 'max-w-[95vw]'
    };

    const themeClasses = {
        dark: {
            bg: 'bg-[#1a1a1a]',
            border: 'border-white/10',
            headerBorder: 'border-surface-700/50',
            title: 'text-text-primary',
            closeBtn: 'text-text-secondary hover:text-text-primary hover:bg-white/5',
            body: 'text-text-secondary',
            footerBg: 'bg-[#1a1a1a]',
            footerBorder: 'border-white/10'
        },
        light: {
            bg: 'bg-white',
            border: 'border-gray-200',
            headerBorder: 'border-gray-100',
            title: 'text-gray-900',
            closeBtn: 'text-gray-500 hover:text-gray-700 hover:bg-black/5',
            body: 'text-gray-600',
            footerBg: 'bg-white',
            footerBorder: 'border-gray-100'
        }
    };

    const theme = themeClasses[variant];

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-surface-950/80 backdrop-blur-sm transition-opacity duration-300"
                onClick={onClose}
            />

            {/* Modal Content */}
            <div
                ref={modalRef}
                className={`
          relative flex flex-col w-full ${widthClasses[size]} 
          ${theme.bg} border ${theme.border} shadow-2xl 
          animate-in fade-in zoom-in-95 duration-200
          ${className}
        `}
            >
                {/* Header */}
                <div className={`flex items-center justify-between px-6 py-4 border-b ${theme.headerBorder}`}>
                    <div className={`text-lg font-medium ${theme.title} tracking-wide`}>
                        {title}
                    </div>
                    <button
                        onClick={onClose}
                        className={`p-1 transition-colors rounded-none ${theme.closeBtn}`}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className={`flex-1 overflow-y-auto p-6 text-sm ${theme.body}`}>
                    {children}
                </div>

                {/* Footer */}
                {footer && (
                    <div className={`flex items-center justify-end gap-3 px-6 py-4 border-t ${theme.footerBorder} ${theme.footerBg}`}>
                        {footer}
                    </div>
                )}
            </div>
        </div >,
        document.body
    );
};
